import React, { useMemo, useState } from "react";

/**
 * DataTable
 * --------------------------------------------------------------------
 * Features:
 * ✅ Search & filter support
 * ✅ Array-safe data rendering
 * ✅ "Add" button
 * ✅ Table-level actions (`tableActions`)
 * ✅ Row-level actions (`rowAction`)
 * ✅ Optional remove button
 * --------------------------------------------------------------------
 */
export default function DataTable({
                                      title,
                                      columns,
                                      data = [],
                                      add = false,
                                      remove = false,
                                      onAdd,
                                      onRemove,
                                      onRowClick,
                                      tableActions = [], // ✅ header buttons (array of {title, onClick})
                                      rowAction = null,  // ✅ single row-level action {title, onClick(row)}
                                  }) {
    const [searchValues, setSearchValues] = useState({});
    const [filterValues, setFilterValues] = useState({});

    // normalize header actions
    const headerActions = Array.isArray(tableActions)
        ? tableActions
        : tableActions
            ? [tableActions]
            : [];

    const handleSearch = (key, value) =>
        setSearchValues((prev) => ({ ...prev, [key]: value.toLowerCase() }));

    const handleFilter = (key, value) =>
        setFilterValues((prev) => ({ ...prev, [key]: value }));

    // unique filter options
    const uniqueOptions = useMemo(() => {
        const opts = {};
        columns.forEach((col) => {
            if (col.filterable) {
                const allValues = data.flatMap((item) => {
                    const val = item[col.key];
                    return Array.isArray(val)
                        ? val.filter(Boolean)
                        : val
                            ? [val]
                            : [];
                });
                opts[col.key] = Array.from(new Set(allValues));
            }
        });
        return opts;
    }, [columns, data]);

    // search + filter
    const filteredData = useMemo(() => {
        return data.filter((item) =>
            columns.every((col) => {
                const cellValue = item[col.key];
                const searchVal = searchValues[col.key];
                const filterVal = filterValues[col.key];

                const valuesArray = Array.isArray(cellValue)
                    ? cellValue.map((v) => String(v).toLowerCase())
                    : [String(cellValue ?? "").toLowerCase()];

                if (searchVal && !valuesArray.some((v) => v.includes(searchVal))) return false;
                if (filterVal && !valuesArray.includes(filterVal.toLowerCase())) return false;

                return true;
            })
        );
    }, [data, columns, searchValues, filterValues]);

    const renderCell = (val) => {
        if (Array.isArray(val)) {
            const clean = val.filter((v) => v !== null && v !== undefined && v !== "");
            if (clean.length === 0) return "None";
            if (clean.length === 1) return clean[0];
            return `${clean[0]} (+${clean.length - 1})`;
        }
        if (val === null || val === undefined || val === "") return "None";
        return val;
    };

    return (
        <div className="mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">{title}</h4>

                <div className="d-flex gap-2">
                    {/* Table-level actions */}
                    {headerActions.map((btn, i) => (
                        <button
                            key={i}
                            className="btn btn-outline-secondary btn-sm"
                            onClick={btn.onClick}
                        >
                            {btn.title}
                        </button>
                    ))}

                    {/* Add button */}
                    {add && (
                        <button className="btn btn-primary btn-sm" onClick={onAdd}>
                            + Add
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <table className="table table-striped align-middle mb-0">
                <thead>
                <tr>
                    {columns.map((col) => (
                        <th key={col.key}>{col.label}</th>
                    ))}
                    {(remove || rowAction) && <th style={{ width: "160px" }}>Actions</th>}
                </tr>

                {/* Search / Filter row */}
                {columns.some((c) => c.searchable || c.filterable) && (
                    <tr>
                        {columns.map((col) => (
                            <th key={col.key}>
                                {col.searchable && (
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: 300 }}
                                        placeholder="Search..."
                                        value={searchValues[col.key] || ""}
                                        onChange={(e) =>
                                            handleSearch(col.key, e.target.value)
                                        }
                                    />
                                )}
                                {col.filterable && uniqueOptions[col.key] && (
                                    <select
                                        className="form-select form-select-sm mt-1"
                                        value={filterValues[col.key] || ""}
                                        onChange={(e) =>
                                            handleFilter(col.key, e.target.value)
                                        }
                                    >
                                        <option value="">All</option>
                                        {uniqueOptions[col.key].map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </th>
                        ))}
                        {(remove || rowAction) && <th></th>}
                    </tr>
                )}
                </thead>

                <tbody>
                {filteredData.length ? (
                    filteredData.map((row, index) => (
                        <tr
                            key={row.id || index}
                            style={{
                                cursor: onRowClick ? "pointer" : "default",
                                transition: "background-color 0.2s",
                            }}
                            onClick={() => onRowClick && onRowClick(row)}
                            onMouseEnter={(e) => {
                                if (onRowClick)
                                    e.currentTarget.style.backgroundColor = "#f1f3f5";
                            }}
                            onMouseLeave={(e) => {
                                if (onRowClick)
                                    e.currentTarget.style.backgroundColor = "";
                            }}
                        >
                            {columns.map((col) => (
                                <td key={col.key}>{renderCell(row[col.key])}</td>
                            ))}

                            {(remove || rowAction) && (
                                <td
                                    className="text-end"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Row-level action */}
                                    {rowAction && (
                                        <button
                                            className="btn btn-outline-secondary btn-sm me-2"
                                            onClick={() => rowAction.onClick(row)}
                                        >
                                            {rowAction.title}
                                        </button>
                                    )}
                                    {/* Remove button */}
                                    {remove && (
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => onRemove?.(row)}
                                        >
                                            Remove
                                        </button>
                                    )}


                                </td>
                            )}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td
                            colSpan={columns.length + (remove || rowAction ? 1 : 0)}
                            className="text-center text-muted"
                        >
                            No data found
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}
